import { useRef, type KeyboardEvent } from "react";
import {
  Backpack,
  BookOpen,
  CloudOff,
  Compass,
  Flag,
  Flower2,
  Languages,
  LoaderCircle,
  Play,
  Shapes,
  Sparkles,
  Star,
  TreePine,
  Users,
  type LucideIcon
} from "lucide-react";

import type { Domain } from "@/domain";
import { PrimaryCommandButton } from "@/ui";
import "./IslandMapScreen.css";

export interface IslandMapProgress {
  readonly domain: Domain;
  readonly completedLevels: number;
  readonly totalLevels: number;
}

export type IslandMapDecorationKind =
  | "flag"
  | "flower"
  | "sparkles"
  | "star"
  | "tree";

export interface IslandMapDecoration {
  readonly id: string;
  readonly domain: Domain;
  readonly label: string;
  readonly kind?: IslandMapDecorationKind;
}

export interface IslandMapRecommendation {
  readonly domain: Domain;
  readonly levelId: string;
  readonly title: string;
}

export interface IslandMapScreenProps {
  readonly dailyRecommendation?: IslandMapRecommendation | null;
  readonly decorations?: readonly IslandMapDecoration[];
  readonly isLoading?: boolean;
  readonly isTemporaryMode?: boolean;
  readonly progress?: readonly IslandMapProgress[];
  readonly onSelectDomain: (domain: Domain) => void;
  readonly onSelectLevel: (levelId: string) => void;
}

interface IslandTargetDefinition {
  readonly detail: string;
  readonly domain: Domain;
  readonly icon: LucideIcon;
  readonly name: string;
}

interface IslandKeyboardNeighbors {
  readonly down: Domain;
  readonly left: Domain;
  readonly right: Domain;
  readonly up: Domain;
}

interface ProgressDisplay {
  readonly completedLevels: number;
  readonly label: string;
  readonly totalLevels: number;
}

const ISLAND_TARGETS: readonly IslandTargetDefinition[] = [
  {
    domain: "chinese",
    name: "语文岛",
    detail: "故事灯塔",
    icon: BookOpen
  },
  {
    domain: "math",
    name: "数学岛",
    detail: "形状市集",
    icon: Shapes
  },
  {
    domain: "english",
    name: "英语岛",
    detail: "单词花园",
    icon: Languages
  },
  {
    domain: "habits",
    name: "习惯岛",
    detail: "校园广场",
    icon: Backpack
  },
  {
    domain: "focus-social",
    name: "专注伙伴岛",
    detail: "森林营地",
    icon: Users
  }
];

const RECOMMENDATION_ACTION_LABELS: Readonly<Record<Domain, string>> = {
  chinese: "去语文岛",
  math: "去数学岛",
  english: "去英语岛",
  habits: "去习惯岛",
  "focus-social": "去伙伴岛"
};

const KEYBOARD_NEIGHBORS: Record<Domain, IslandKeyboardNeighbors> = {
  chinese: {
    up: "math",
    right: "math",
    down: "focus-social",
    left: "focus-social"
  },
  math: {
    up: "chinese",
    right: "english",
    down: "habits",
    left: "chinese"
  },
  english: {
    up: "math",
    right: "habits",
    down: "habits",
    left: "math"
  },
  habits: {
    up: "math",
    right: "english",
    down: "english",
    left: "focus-social"
  },
  "focus-social": {
    up: "chinese",
    right: "habits",
    down: "habits",
    left: "chinese"
  }
};

const DECORATION_ICONS: Record<IslandMapDecorationKind, LucideIcon> = {
  flag: Flag,
  flower: Flower2,
  sparkles: Sparkles,
  star: Star,
  tree: TreePine
};

const ROUTE_DOT_KEYS = [
  "chinese-math-1",
  "chinese-math-2",
  "chinese-math-3",
  "chinese-math-4",
  "math-english-1",
  "math-english-2",
  "math-english-3",
  "english-habits-1",
  "english-habits-2",
  "english-habits-3",
  "habits-focus-1",
  "habits-focus-2"
] as const;

function getKeyboardDestination(
  domain: Domain,
  key: string
): Domain | undefined {
  const neighbors = KEYBOARD_NEIGHBORS[domain];

  switch (key) {
    case "ArrowUp":
      return neighbors.up;
    case "ArrowRight":
      return neighbors.right;
    case "ArrowDown":
      return neighbors.down;
    case "ArrowLeft":
      return neighbors.left;
    case "Home":
      return "chinese";
    case "End":
      return "focus-social";
    default:
      return undefined;
  }
}

function getProgressDisplay(
  value: IslandMapProgress | undefined
): ProgressDisplay | undefined {
  if (
    value === undefined ||
    !Number.isFinite(value.totalLevels) ||
    value.totalLevels <= 0
  ) {
    return undefined;
  }

  const totalLevels = Math.max(1, Math.floor(value.totalLevels));
  const rawCompletedLevels = Number.isFinite(value.completedLevels)
    ? Math.floor(value.completedLevels)
    : 0;
  const completedLevels = Math.min(
    totalLevels,
    Math.max(0, rawCompletedLevels)
  );

  return {
    completedLevels,
    totalLevels,
    label: completedLevels + "/" + totalLevels + " 关"
  };
}

function getIslandName(domain: Domain): string {
  return (
    ISLAND_TARGETS.find((target) => target.domain === domain)?.name ?? "学习岛"
  );
}

interface DecorationOverlayProps {
  readonly decorations: readonly IslandMapDecoration[];
  readonly islandName: string;
}

function DecorationOverlay({
  decorations,
  islandName
}: DecorationOverlayProps) {
  if (decorations.length === 0) {
    return null;
  }

  return (
    <div
      aria-label={islandName + "的装饰"}
      className="island-map__decorations"
      role="group"
    >
      {decorations.map((decoration) => {
        const kind = decoration.kind ?? "star";
        const DecorationIcon = DECORATION_ICONS[kind];

        return (
          <span
            aria-label={decoration.label}
            className="island-map__decoration"
            data-kind={kind}
            key={decoration.id}
            role="img"
            title={decoration.label}
          >
            <DecorationIcon aria-hidden="true" />
          </span>
        );
      })}
    </div>
  );
}

interface RecommendationPanelProps {
  readonly recommendation: IslandMapRecommendation | null;
  readonly onSelectLevel: (levelId: string) => void;
}

function RecommendationPanel({
  recommendation,
  onSelectLevel
}: RecommendationPanelProps) {
  const recommendationLabel =
    recommendation === null
      ? "今日航线：今天自由探险"
      : "今日航线：" +
        recommendation.title +
        "，" +
        getIslandName(recommendation.domain);

  return (
    <aside
      aria-label={recommendationLabel}
      aria-live="polite"
      className="island-map__recommendation"
    >
      <div className="island-map__recommendation-copy">
        <h2>今日航线</h2>
        {recommendation === null ? (
          <p className="island-map__recommendation-empty" role="status">
            今天自由探险
          </p>
        ) : (
          <>
            <strong title={recommendation.title}>
              {recommendation.title}
            </strong>
            <span>{getIslandName(recommendation.domain)}</span>
          </>
        )}
      </div>

      {recommendation === null ? null : (
        <PrimaryCommandButton
          aria-label={"开始推荐活动：" + recommendation.title}
          className="island-map__recommendation-action"
          icon={Play}
          onClick={() => onSelectLevel(recommendation.levelId)}
        >
          {RECOMMENDATION_ACTION_LABELS[recommendation.domain]}
        </PrimaryCommandButton>
      )}
    </aside>
  );
}

export function IslandMapScreen({
  dailyRecommendation = null,
  decorations = [],
  isLoading = false,
  isTemporaryMode = false,
  progress = [],
  onSelectDomain,
  onSelectLevel
}: IslandMapScreenProps) {
  const buttonRefs = useRef<
    Partial<Record<Domain, HTMLButtonElement | null>>
  >({});
  const progressByDomain = new Map<Domain, IslandMapProgress>();

  for (const domainProgress of progress) {
    progressByDomain.set(domainProgress.domain, domainProgress);
  }

  function handleTargetKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    domain: Domain
  ) {
    const destination = getKeyboardDestination(domain, event.key);

    if (destination === undefined) {
      return;
    }

    event.preventDefault();
    buttonRefs.current[destination]?.focus();
  }

  return (
    <section
      aria-busy={isLoading}
      aria-labelledby="island-map-title"
      className="island-map"
      data-temporary-mode={isTemporaryMode || undefined}
    >
      <header className="island-map__heading">
        <Compass aria-hidden="true" />
        <div>
          <p>小小启航岛</p>
          <h1 id="island-map-title">岛屿地图</h1>
        </div>
      </header>

      {isLoading ? (
        <div
          aria-label="正在准备岛屿地图"
          className="island-map__loading"
          role="status"
        >
          <LoaderCircle aria-hidden="true" />
          <strong>海岛准备中</strong>
        </div>
      ) : (
        <>
          <RecommendationPanel
            onSelectLevel={onSelectLevel}
            recommendation={dailyRecommendation}
          />

          <div className="island-map__stage">
            <img
              alt=""
              aria-hidden="true"
              className="island-map__surface"
              draggable={false}
              fetchPriority="high"
              height="1024"
              src="/assets/world/campus-islands-map.webp"
              width="1536"
            />

            <div aria-hidden="true" className="island-map__route">
              {ROUTE_DOT_KEYS.map((dotKey) => (
                <span className="island-map__route-dot" key={dotKey} />
              ))}
            </div>

            <div
              aria-label="五座学习岛"
              className="island-map__targets"
              role="group"
            >
              {ISLAND_TARGETS.map((target) => {
              const Icon = target.icon;
              const targetProgress = getProgressDisplay(
                progressByDomain.get(target.domain)
              );
              const isRecommended =
                dailyRecommendation?.domain === target.domain;
              const progressAnnouncement =
                targetProgress === undefined
                  ? "尚未开始"
                  : "已完成 " +
                    targetProgress.completedLevels +
                    " 关，共 " +
                    targetProgress.totalLevels +
                    " 关";
              const targetDecorations = decorations.filter(
                (decoration) => decoration.domain === target.domain
              );

              return (
                <div
                  className="island-map__target-anchor"
                  data-domain={target.domain}
                  key={target.domain}
                >
                  <button
                    aria-label={
                      target.name +
                      "，" +
                      progressAnnouncement +
                      (isRecommended ? "，今日推荐" : "")
                    }
                    className="island-map__target"
                    data-recommended={isRecommended || undefined}
                    onClick={() => onSelectDomain(target.domain)}
                    onKeyDown={(event) =>
                      handleTargetKeyDown(event, target.domain)
                    }
                    ref={(node) => {
                      buttonRefs.current[target.domain] = node;
                    }}
                    type="button"
                  >
                    {isRecommended ? (
                      <span className="island-map__recommended-badge">
                        今日推荐
                      </span>
                    ) : null}
                    <span className="island-map__target-icon">
                      <Icon aria-hidden="true" />
                    </span>
                    <span className="island-map__target-copy">
                      <strong>{target.name}</strong>
                      <span>{target.detail}</span>
                    </span>
                    <span className="island-map__target-progress">
                      {targetProgress?.label ?? "尚未开始"}
                    </span>
                  </button>

                  <DecorationOverlay
                    decorations={targetDecorations}
                    islandName={target.name}
                  />
                </div>
              );
              })}
            </div>

            <figure className="island-map__guide">
              <img
                alt="向大家挥手的点点"
                draggable={false}
                height="768"
                loading="lazy"
                src="/assets/characters/diandian.webp"
                width="512"
              />
              <figcaption className="island-map__bottom-hint">
                <span className="island-map__bottom-hint-prefix">下一站，</span>
                由你选择
              </figcaption>
            </figure>
          </div>

          {isTemporaryMode ? (
            <aside
              aria-label="临时模式"
              className="island-map__temporary-notice"
              role="status"
            >
              <CloudOff aria-hidden="true" />
              <span>
                <strong>临时游玩</strong>
                <span>这次进度不会保存</span>
              </span>
            </aside>
          ) : null}
        </>
      )}
    </section>
  );
}
